import { useRef, useState } from "react";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { Alert, Button, DetailList, Dialog, Field } from "../../ui";
import { failure } from "../workspace/behavior";
import { money } from "../workspace/model";
import type { MobilePaymentRow } from "./views";

type Cancellation = { status: string; resultMessage?: string | null };
type RequestMemory = { key: string; reason: string; outcome: "pending" | "unknown" | "failed" | "success" };

export function MobilePaymentCancellationDialog({ row, onClose }: { readonly row: MobilePaymentRow; readonly onClose: () => void }) {
  const { apiClient, queryClient } = useAdminApplication();
  const session = useAdminSession();
  const cacheKey = ["admin", session.sessionEpoch, "mobile-payment-cancellation", row.id] as const;
  const saved = queryClient.getQueryData<RequestMemory>(cacheKey);
  const request = useRef<RequestMemory | undefined>(saved);
  const busy = useRef(false);
  const [reason, setReason] = useState(saved?.reason ?? "");
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<RequestMemory["outcome"] | "none">(saved?.outcome ?? "none");
  const [message, setMessage] = useState("");
  const allowed = row.cancelable && session.authSession?.authorities?.includes("PAYMENT_CANCEL");
  const remember = (value: RequestMemory["outcome"]) => {
    setOutcome(value);
    if (request.current) {
      request.current = { ...request.current, outcome: value };
      queryClient.setQueryData(cacheKey, request.current);
    }
  };
  const apply = (result: Cancellation | null) => {
    if (!result) { setMessage("취소 이력이 확인되지 않았습니다. 최신 목록을 확인해 주세요."); return; }
    if (result.status === "COMPLETED") {
      remember("success");
      setMessage("휴대폰 소액결제가 전액 취소되었습니다.");
    } else if (result.status === "FAILED") {
      remember("failed");
      request.current = undefined;
      queryClient.removeQueries({ queryKey: cacheKey, exact: true });
      setConfirmed(false);
      setMessage(result.resultMessage || "취소가 실패했습니다. 실패 사유를 확인한 뒤 다시 요청해 주세요.");
    } else {
      remember("unknown");
      setMessage("취소 결과를 확인하는 중입니다. 다시 요청하지 말고 최신 결과를 조회해 주세요.");
    }
  };
  const refresh = async () => {
    if (busy.current) return;
    busy.current = true; setPending(true);
    try {
      apply((await apiClient.get<Cancellation | null>(`/api/v1/admin/bo/mobile-payments/${row.id}/cancellation`, { cache: "no-store" })).data);
      await queryClient.invalidateQueries({ queryKey: ["admin", session.sessionEpoch, "business-operations"] });
    } catch (error) { setMessage(failure(error).message); }
    finally { busy.current = false; setPending(false); }
  };
  const submit = async () => {
    if (busy.current || !allowed || !confirmed || outcome === "unknown" || outcome === "success" || outcome === "pending") return;
    if (reason.trim().length < 5 || reason.trim().length > 1000) { setMessage("취소 사유를 5~1,000자로 입력해 주세요."); return; }
    request.current ??= { key: globalThis.crypto.randomUUID(), reason: reason.trim(), outcome: "pending" };
    remember("pending"); busy.current = true; setPending(true); setMessage("");
    try {
      apply((await apiClient.post<Cancellation>(`/api/v1/admin/bo/mobile-payments/${row.id}/cancel`, { reason: request.current.reason }, { headers: { "Idempotency-Key": request.current.key }, cache: "no-store" })).data);
      await queryClient.invalidateQueries({ queryKey: ["admin", session.sessionEpoch, "business-operations"] });
    } catch (error) {
      const status = (error as { status?: number }).status;
      const known = status && [400, 401, 403, 404, 422].includes(status) && !failure(error).reconcile;
      remember(known ? "failed" : "unknown");
      if (known) { request.current = undefined; queryClient.removeQueries({ queryKey: cacheKey, exact: true }); setConfirmed(false); }
      setMessage(failure(error).message);
    } finally { busy.current = false; setPending(false); }
  };
  const uncertain = outcome === "unknown" || outcome === "pending";
  return <Dialog open title="휴대폰 소액결제를 전액 취소할까요?" description="원결제 금액 전체를 취소합니다. 처리 결과를 확인하기 전에는 다시 요청하지 마세요." onClose={onClose} closeDisabled={pending} secondaryAction={<Button variant="secondary" disabled={pending} onClick={onClose}>닫기</Button>} primaryAction={<>
    {uncertain ? <Button disabled={pending} onClick={() => void refresh()}>최신 결과 조회</Button> : outcome !== "success" ? <Button variant="danger" disabled={!allowed || pending || !confirmed} onClick={() => void submit()}>전체 취소</Button> : null}
  </>}>
    <DetailList items={[{ label: "회원명", value: row.memberName || "—" }, { label: "휴대폰번호", value: row.phoneNumber || "—" }, { label: "취소 금액", value: money(row.amount) }]}/>
    {message ? <Alert persistent variant={outcome === "success" ? "success" : "danger"} title={outcome === "success" ? "취소 완료" : "처리 결과"}>{message}</Alert> : null}
    {!uncertain && outcome !== "success" ? <>
      <Field control="textarea" id="mobile-cancel-reason" label="취소 사유" required value={reason} maxLength={1000} disabled={pending} onChange={e => { setReason(e.target.value); setConfirmed(false); }}/>
      <label><input type="checkbox" checked={confirmed} disabled={pending} onChange={e => setConfirmed(e.target.checked)}/> 결제 금액 전체를 취소하는 것을 확인했습니다.</label>
    </> : null}
  </Dialog>;
}
